<template>
  <div class="action" :class="{ oneLine, completed }" @click="actionClicked">
    <div class="gridElement h-100 w-100">
      <div class="id flex align-items-center header" :class="recentlyChanged">
        <IDBadge
          :id="action.id"
          style="min-width: calc(8ch + 10.5px)"
          class="text-center pointerAll"
          :prefix="idPrefix(action.__typename)"
          @click="idBadgeClicked"
        />
      </div>
      <div class="flex align-items-center header">
        <slot name="assignee">
          <div class="assignee">
            <UserBadge
              :user="action.assignee"
              class="bounceTag"
              @click="setUserFilter(action.assignee, $event)"
            />
          </div>
        </slot>

        <div v-if="$slots.leadingHeaderAdditions" class="pr-1">
          <slot name="leadingHeaderAdditions" />
        </div>

        <div v-if="oneLine" class="oneLineContent">
          <slot name="oneLineContent" />
        </div>

        <div class="headerAdditions grow">
          <slot name="headerAdditions" />
        </div>

        <slot name="deadline">
          <div class="deadline">
            <ActionDeadlineBadge :action="action" />
          </div>
        </slot>

        <slot name="status">
          <div class="status pl-1 mt-1" :class="{ pointerNone: readOnly }">
            <ActionStatusCircle
              :action="action"
              :disable-popup="readOnly"
              style="height: 1.5rem; aspect-ratio: 1/1"
            >
              <template v-if="userIdForAnonymStatusChange == null" #default="{ resetTimer }">
                <InputRow
                  v-model="userIdForAnonymStatusChange"
                  name="owner"
                  :display-name="t('global.user')"
                  required
                >
                  <template #default="{ field: inputField }">
                    <SearchSelect
                      v-bind="inputField"
                      v-model="userIdForAnonymStatusChange"
                      :state="false"
                      :mode="SelectMode.USER"
                      :placeholder="
                        t('components.shared.searchSelect.UserSelect.selectUser').toString()
                      "
                      :filters="
                        siteId == null
                          ? undefined
                          : {
                              hasRightsOnSite: {
                                siteIds: [siteId],
                                rights: SQDC_ASSING_MINIMUM_RIGHTS,
                              },
                            }
                      "
                      class="w-100"
                      :hide-jump-to-origin="true"
                      :required="true"
                      @hide="resetTimer(700)"
                    />
                  </template>
                </InputRow>
                <div class="py-1">
                  {{ t("components.event-action.action.ActionComponent.selectUser") }}
                </div>
              </template>
            </ActionStatusCircle>
          </div>
        </slot>
      </div>
      <div
        class="linked flex align-items-start justify-content-between px-2"
        :class="recentlyChanged"
      />
      <div v-if="!oneLine" class="content pr-4">
        <slot />

        <GithubPullRequestInfo
          v-if="'description' in action && action.description === 'Bitte Merge Request bearbeiten!'"
          :task-id="action.id"
        ></GithubPullRequestInfo>
        <i
          class="fas fa-angle-up collapseButton mr-1"
          style="font-size: 1.5em"
          @click.stop="handleOneLine"
        />
      </div>
    </div>
    <slot name="editComponent" />
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { type PropType, ref, computed, inject } from "vue";
import { DateTime } from "luxon";
import {
  ActionType,
  type ActionListActionFieldsFragment,
  type EventListEventFieldsFragment,
} from "@/graphql";
import UserBadge from "@/components/badges/UserBadge.vue";
import IDBadge from "../../badges/IDBadge.vue";
import ActionDeadlineBadge from "./ActionDeadlineBadge.vue";
import { CreatorFilter, RecentlyChangedFilter } from "../filter/filterImplementations";
import { actionIdPrefix } from "@/components/event-action/event/EventActionIdPrefix";
import {
  RecentlyChangedAlternativeEnum,
  getRecentlyChangedAlternative,
} from "@/views/event-action/EventActionBoardInjectionKeys";
import store from "@/store";
import ActionStatusCircle from "../issue/ActionStatusCircle.vue";
import { recentTime } from "../helper/EventBoardRecentlyChangedHelper";
import usePropsAsDefault from "@/functions/usePropsAsDefault";
import getDefaultEABFilter from "@/views/event-action/EventActionBoardDefaultFilter";
import { ActionFilterInjectionKey } from "../filter/FilterTypes";
import GithubPullRequestInfo from "../../../modules/github/components/GithubPullRequestInfo.vue";
import { useProvidedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";
import { hasRight } from "@/functions/authorizationState";
import InputRow from "@/components/shared/form/InputRow.vue";
import { SelectMode } from "@/components/shared/searchSelects/SelectDefinitions";
import SearchSelect from "@/components/shared/searchSelects/SearchSelect.vue";
import { SQDC_ASSING_MINIMUM_RIGHTS } from "../task/helper/SQDCHelper";

const props = defineProps({
  action: {
    type: Object as PropType<ActionListActionFieldsFragment>,
    required: true,
  },
  completed: {
    type: Boolean,
    default: false,
  },
  archived: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["open-edit-component", "click", "oneLine"]);

const userIdForAnonymStatusChange = computed({
  get() {
    return store.getters["taskComment/getTaskCommentatorId"];
  },
  set(value: number | null) {
    store.dispatch("taskComment/setTaskCommentatorId", value);
  },
});

const oneLine = usePropsAsDefault({ dynamicProps: computed(() => props.completed) });
const { defaultActionFilters } = getDefaultEABFilter();
const actionFiltersDefault = ref(defaultActionFilters);

const actionFilters = inject(ActionFilterInjectionKey, actionFiltersDefault);

const actionClicked = (event: Event) => {
  if (oneLine.value === true) {
    oneLine.value = false;
  } else {
    emit("click", event, oneLine.value);
  }
};

const recentlyChangedFilter = new RecentlyChangedFilter(recentTime.value);

const recentlyChangedAlternativeKey = ref(getRecentlyChangedAlternative());

const recentlyChanged = computed(() => {
  if (!recentlyChangedFilter.isActionActive(props.action)) {
    return {};
  }
  switch (recentlyChangedAlternativeKey.value) {
    case RecentlyChangedAlternativeEnum.NORMAL:
      return { recentlyChanged: true };
    case RecentlyChangedAlternativeEnum.GREEN_WAVES:
      return { recentlyChangedAlternative: true };
    default:
      return undefined;
  }
});

function idPrefix(type?: string) {
  if (type == null) {
    return "";
  }
  return actionIdPrefix[type as ActionType];
}

const idBadgeClicked = () => {
  emit("open-edit-component");
};
function setUserFilter(owner: EventListEventFieldsFragment["owner"], evt: MouseEvent) {
  if ((evt.ctrlKey || evt.metaKey) && owner != null) {
    actionFilters.value = {
      updatedAt: DateTime.now(),
      actionFilters: [new CreatorFilter([owner?.id])],
    };
  }
}
function handleOneLine() {
  oneLine.value = true;
}

const { siteId } = useProvidedBoardContext();
const readOnly = computed(() => props.archived || !hasRight("sqdc.write", siteId.value));

const { t } = useTranslation();
</script>

<style lang="scss" src="@/components/event-action/shared.scss"></style>

<style lang="scss" scoped>
.action {
  color: var(--p-black) !important;
  &.oneLine {
    cursor: pointer;
    padding-bottom: 0;
  }

  &.completed.oneLine {
    opacity: 0.6;
  }

  .header {
    .headerAdditions {
      text-align: right;
    }

    .oneLineContent {
      padding-left: 0.25rem;
      padding-right: 0.25rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .content {
    position: relative;

    .collapseButton {
      position: absolute;
      bottom: 0;
      right: 6px;
      padding: 3px;
      cursor: pointer;
    }
  }
}
.pointerAll {
  pointer-events: all;
}
.pointerNone {
  pointer-events: none;
}
</style>
